/**
 * User-kind resolution from `/me`.
 *
 * In the target architecture (full SPA served from CloudFront, plus a native iOS
 * client) there is no Express render step and no server-injected `loggedUser`
 * global — so user-kind is NOT available synchronously on first paint. `/me` is
 * the single cross-client source of truth; every client (web, iOS) reads the
 * same `user-type` field the backend derives from role.
 *
 * These helpers read the `me` store slice (fed by `/me`) and apply a default-deny
 * rule for the web shell: nothing staff-only renders until `/me` has confirmed
 * the user is internal.
 */
export interface MeSlice {
  userType?: 'Internal' | 'External' | string | null;
  loaded?: boolean;
  projects?: Array<{ id: number | string }>;
}

/**
 * True only once `/me` has loaded AND reported "Internal". Before `/me` resolves
 * — and for external users — this is false, so the staff shell, the staff-only
 * global modals (e.g. LogActivity, which crashes on a client's missing
 * `user_role`), and the Users page never render. Default-deny by construction.
 */
export const isInternalUser = (me?: MeSlice | null): boolean =>
  Boolean(me && me.loaded && me.userType === 'Internal');

/**
 * Confirmed external: `/me` has loaded AND reported "External". Used for the
 * client landing redirect, where the safe default for the unknown/loading state
 * is "do not redirect" — so an internal user is never bounced to a client
 * landing before `/me` resolves.
 */
export const isExternalUser = (me?: MeSlice | null): boolean =>
  Boolean(me && me.loaded && me.userType === 'External');

/** Project ids from `/me`, for the client landing rule. */
export const meProjectIds = (me?: MeSlice | null): Array<number | string> =>
  Array.isArray(me?.projects) ? (me as MeSlice).projects!.map((p) => p.id) : [];
